<style src="./style.scss">
</style>

<script>
  import Button from "@components/button";
  import Header from "@components/header";
  import TextInput from "@components/textInput";
  import Toast from "@components/toast";

  import { Router, Link, Route } from "svelte-navigator";
  import { navigate, useLocation } from "svelte-navigator";
  import { email as storedEmail, authType } from "@store";
  import {
    parseQuery,
    generateCaptchaTokenOnSubmit,
    TERMS_LINK,
    PRIVACITY_LINK,
  } from "@utils";
  import { query, mutation } from "svelte-apollo";

  import { VERIFY_EMAIL_QUERY } from "./verifyEmail.query.ts";
  import { CREATE_ACCOUNT_MUTATION } from "./createAccount.mutation.ts";
  import { LOGIN_MUTATION } from "./login.mutation.ts";
  import { captureException } from '@integrations/sentry.ts';
  import { saveToken, requestPasswordChange, saveEmail } from "@services/auth";

  const location = useLocation();

  const queryParams = parseQuery($location.search);

  export let email = "";
  export let password = "";
  export let passwordConfirm = "";
  export let step = "email";
  export let isFetching = false;

  const createAccountMutation = mutation(CREATE_ACCOUNT_MUTATION);
  const login = mutation(LOGIN_MUTATION);
  const verifyEmailQuery = query(VERIFY_EMAIL_QUERY, {
    variables: { input: { email } },
    skip: true,
  });

  if (queryParams.access_token) {
    saveToken(queryParams.access_token);

    navigate(`/checkout${$location.search ? `/${$location.search}` : ""}`);
  }

  let passwordFieldError = false;

  let isValidEmail = (value) => {
    const pattern = /\S+@\S+\.\S+/;
    return pattern.test(value);
  };

  let message = {
    visible: false,
    text: "",
    toastType: "",
    size: "small",
  };

  function showMessage(
    text,
    toastType,
    size
  ) {
    message = {
      toastType,
      text,
      visible: true,
      size: size || "small",
    };
  }

  function clearMessage() {
    passwordFieldError = false;
    message = { visible: false, text: "", toastType: "alert" };
  }

  async function verifyEmailStep() {
    if (!isValidEmail(email)) {
      showMessage("Este e-mail não é válido.", "alert");
      return;
    }

    try {
      isFetching = true;

      const { data } = await verifyEmailQuery.refetch({ input: { email } });

      const { status } = data.verifyEmail;

      if (status === "invalid-email") {
        showMessage("Este e-mail não é válido.", "alert");
        return;
      }

      if (status === "missing-password") {
        storedEmail.set(email);
        saveEmail(email);

        navigate(`/create-account?email=${email}`, {
          state: { redirectRoute: "/overview" },
        });

        return;
      }

      step = status === "new-account" ? "create" : "password";
    } catch (error) {
      captureException(error);
    } finally {
      clearMessage();
      isFetching = false;
    }
  }

  async function passwordRecovery() {
    showMessage(
      `Enviamos um e-mail para ${email} com as instruções para você redefinir sua senha.`,
      "info",
      "large"
    );

    await requestPasswordChange(email);
  }

  async function auth() {
    isFetching = true;
    try {
      const { data } = await login({
        variables: {
          input: {
            email,
            password,
          },
        },
      });

      const { accessToken } = data.login;

      if (accessToken) {
        clearMessage();

        saveToken(accessToken);

        const route = $location.state?.from
          ? $location.state.from
          : `/overview${$location.search ? `/${$location.search}` : ""}`;

        navigate(route);

        storedEmail.set(email);
        saveEmail(email);
      }
      authType.set("sign-in");
    } catch (error) {
      captureException(error);
      showMessage("Seu e-mail ou sua senha esta errado.", "alert");
    } finally {
      isFetching = false;
    }
  }

  async function createAccount() {
    if (password !== passwordConfirm) {
      showMessage("As senhas não conferem.", "alert");
      passwordFieldError = true;
      return;
    }

    if (password.length < 6) {
      showMessage("Cadastre uma senha com no mínimo 6 caracteres.", "alert");
      passwordFieldError = true;
      return;
    }

    isFetching = true;
    clearMessage();

    try {
      const captchaToken = await generateCaptchaTokenOnSubmit();

      const { data } = await createAccountMutation({
        variables: {
          input: {
            email,
            password,
            passwordConfirmation: passwordConfirm,
          },
        },
        context: {
          headers: { "X-Captcha": captchaToken },
        },
      });

      const { accessToken } = data.createAccount;

      if (accessToken) {
        isFetching = false;
        saveToken(data.access_token);

        storedEmail.set(email);
        saveEmail(email);
        authType.set("sign-up");
        navigate(`/overview${$location.search ? `/${$location.search}` : ""}`);
      }

      return;
    } catch (error) {
      captureException(error);
      if (error.response.data) {
        const { message } = error.response.data;
        if (message === "Invalid e-mail") {
          showMessage(
            "E-mail inválido, verifique o e-mail e tente novamente",
            "alert"
          );

          step = "email";
          password = "";
          passwordConfirm = "";
          return;
        }
      }

      showMessage("Desculpe, tente mais tarde.", "alert");
    } finally {
      isFetching = false;
    }
  }

  function goBack() {
    password = "";
    passwordConfirm = "";

    step = "email";
  }
</script>

<div class="auth">
  <Header goBack="{goBack}" showBackButton="{step !== 'email'}" />

  <div class="auth__content">
    <Toast
      message="{message.text}"
      toastType="{message.toastType}"
      visible="{message.visible}"
      size="{message.size}"
      onClose="{clearMessage}" />

    {#if step === "email"}
      <h1>Qual é o seu e-mail?</h1>

      <form on:submit|preventDefault="{verifyEmailStep}">
        <TextInput
          label="Endereço de e-mail"
          placeholder="Endereço de e-mail"
          disabled="{isFetching}"
          bind:value="{email}"
          type="email" />
        <div class="button-container">
          <Button
            title="{isFetching ? 'Aguarde' : 'Continuar'}"
            disabled="{isFetching || !isValidEmail(email)}" />
        </div>
      </form>
    {/if}

    {#if step === "password"}
      <h1>Qual é a sua senha?</h1>
      <form on:submit|preventDefault="{auth}">
        <TextInput
          label="Senha"
          placeholder="Senha"
          disabled="{isFetching}"
          error="{passwordFieldError}"
          bind:value="{password}"
          passwordInput="{true}" />
        <a class="password-recovery" on:click="{passwordRecovery}"
          >Esqueceu sua senha?</a>
        <div class="button-container">
          <Button
            title="{isFetching ? 'Aguarde' : 'Entrar na conta'}"
            disabled="{!password || isFetching}" />
        </div>
      </form>
    {/if}

    {#if step === "create"}
      <h1>Por favor, crie uma senha</h1>
      <form on:submit|preventDefault="{createAccount}">
        <TextInput
          label="Senha"
          placeholder="Senha"
          disabled="{isFetching}"
          error="{passwordFieldError}"
          bind:value="{password}"
          passwordInput="{true}"
          emptyMessage="A senha está em branco." />

        <TextInput
          label="Confirmar senha"
          placeholder="Confirmar senha"
          disabled="{isFetching}"
          emptyMessage="A senha está em branco."
          bind:value="{passwordConfirm}"
          error="{passwordFieldError}"
          passwordInput="{true}" />

        <div class="button-container">
          <Button
            title="{isFetching ? 'Aguarde' : 'Criar uma conta'}"
            disabled="{!password || isFetching}" />
        </div>
      </form>

      <p class="auth__footer">
        Ao criar uma conta no CNA Go, eu aceito os <a
          href="{TERMS_LINK}"
          target="_blank">Termos e Condições</a>
        e a
        <a href="{PRIVACITY_LINK}" target="_blank">Política de Privacidade.</a>
      </p>
    {/if}
  </div>
</div>
